import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { Btn } from "../components/UI";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState("checking"); // checking | paid | timeout | error
  const attempts = useRef(0);
  const sessionId = params.get("session_id");

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let stop = false;
    const poll = async () => {
      if (stop) return;
      if (attempts.current++ > 10) { setState("timeout"); return; }
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        if (r.data.payment_status === "paid") { setState("paid"); return; }
        if (["failed", "expired"].includes(r.data.payment_status)) { setState("error"); return; }
      } catch { setState("error"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
  }, [sessionId]);

  return (
    <div className="flex min-h-[60vh] items-center justify-center" data-testid="payment-success-page">
      <div className="max-w-md w-full rounded-2xl border border-[#EFEBE0] bg-white p-8 text-center shadow-sm">
        {state === "checking" && (<>
          <Loader2 size={40} className="mx-auto animate-spin text-[#7B6157]" />
          <h1 className="font-heading text-xl font-bold text-[#3E2723] mt-4">Confirming your payment…</h1>
          <p className="text-sm text-[#7B6157] mt-2">Please wait while we verify the transaction with Stripe.</p>
        </>)}
        {state === "paid" && (<>
          <CheckCircle2 size={44} className="mx-auto text-[#2E7D32]" />
          <h1 className="font-heading text-xl font-bold text-[#3E2723] mt-4" data-testid="payment-confirmed">Payment confirmed</h1>
          <p className="text-sm text-[#7B6157] mt-2">Your invoice is paid and your license has been extended. Thank you!</p>
          <Btn data-testid="back-to-billing-btn" className="mt-5" onClick={() => navigate("/billing")}>Back to Billing</Btn>
        </>)}
        {(state === "timeout" || state === "error") && (<>
          <XCircle size={44} className="mx-auto text-[#C62828]" />
          <h1 className="font-heading text-xl font-bold text-[#3E2723] mt-4">{state === "timeout" ? "Still processing" : "Payment not confirmed"}</h1>
          <p className="text-sm text-[#7B6157] mt-2">{state === "timeout" ? "The payment is taking longer than expected - check your Billing page in a minute or contact support." : "We couldn't confirm this payment. If you were charged, contact support."}</p>
          <Btn className="mt-5" onClick={() => navigate("/billing")}>Back to Billing</Btn>
        </>)}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  const navigate = useNavigate();
  return (
    <div className="flex min-h-[60vh] items-center justify-center" data-testid="payment-cancel-page">
      <div className="max-w-md w-full rounded-2xl border border-[#EFEBE0] bg-white p-8 text-center shadow-sm">
        <XCircle size={44} className="mx-auto text-[#8D5A00]" />
        <h1 className="font-heading text-xl font-bold text-[#3E2723] mt-4">Payment cancelled</h1>
        <p className="text-sm text-[#7B6157] mt-2">No charge was made. Your invoice is still open on the Billing page.</p>
        <Btn className="mt-5" onClick={() => navigate("/billing")}>Back to Billing</Btn>
      </div>
    </div>
  );
}
